import { useCallback, useEffect, useRef, useState, type RefObject } from "react";
import type { BoardParticipant } from "../components/board/BoardParticipants";
import type { BoardViewportState } from "../lib/board/boardViewport";

type ViewportApi = {
  applyFollowViewport: (next: BoardViewportState, opts?: { syncReact?: boolean }) => void;
  getLiveViewport: () => BoardViewportState;
  viewport: BoardViewportState;
  viewportRef: RefObject<HTMLDivElement | null>;
};

type PresenceLike = {
  getPeerViewport?: (peerId: string) => BoardViewportState | null | undefined;
  getPeerCursor?: (peerId: string) => { x: number; y: number } | null | undefined;
  refreshPeerCursors?: () => void;
} | null;

type StagePoint = { x: number; y: number };

/**
 * Сглаживание follow. На тачпаде пакеты реже — большой smooth даёт «гигантскую» паузу.
 * Без edge-pan можно почти snap'ать к viewport ведущего.
 */
const FOLLOW_SMOOTH_MS = 14;
const FOLLOW_CATCHUP_PAN_PX = 48;
const FOLLOW_SETTLE_PAN_PX = 0.3;
const FOLLOW_SETTLE_SCALE = 0.0006;

function isValidViewport(vp: BoardViewportState | null | undefined): vp is BoardViewportState {
  return Boolean(
    vp &&
      typeof vp.panX === "number" &&
      typeof vp.panY === "number" &&
      typeof vp.scale === "number" &&
      Number.isFinite(vp.panX) &&
      Number.isFinite(vp.panY) &&
      Number.isFinite(vp.scale) &&
      vp.scale > 0
  );
}

function isValidCursor(c: StagePoint | null | undefined): c is StagePoint {
  return Boolean(
    c && typeof c.x === "number" && typeof c.y === "number" && Number.isFinite(c.x) && Number.isFinite(c.y)
  );
}

/**
 * Следование только за viewport ведущего (без edge-pan по курсору —
 * он давал дёрганье при движении курсора обратно от края).
 */
export function useFollowPresenter(
  vp: ViewportApi,
  presenceRef: RefObject<PresenceLike>,
  ready: boolean,
  participants: BoardParticipant[]
) {
  const [followPeerId, setFollowPeerId] = useState<string | null>(null);
  const followPeerIdRef = useRef<string | null>(null);
  followPeerIdRef.current = followPeerId;
  const applyingFollowRef = useRef(false);
  const vpRef = useRef(vp);
  vpRef.current = vp;

  const targetRef = useRef<BoardViewportState | null>(null);
  const rafRef = useRef(0);
  const lastTickRef = useRef(0);
  const reactEveryRef = useRef(0);

  const stopFollowLoop = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
    }
    targetRef.current = null;
    lastTickRef.current = 0;
  }, []);

  const clearFollow = useCallback(() => {
    stopFollowLoop();
    followPeerIdRef.current = null;
    setFollowPeerId(null);
  }, [stopFollowLoop]);

  const ensureFollowLoop = useCallback(() => {
    if (rafRef.current) return;

    const tick = (now: number) => {
      rafRef.current = 0;
      if (!followPeerIdRef.current) return;
      const target = targetRef.current;
      if (!target) return;

      const last = lastTickRef.current || now;
      lastTickRef.current = now;
      const dt = Math.min(48, Math.max(0, now - last));
      let k = 1 - Math.exp(-dt / FOLLOW_SMOOTH_MS);

      const cur = vpRef.current.getLiveViewport();
      // Большой разрыв после редких wheel-пакетов — догоняем жёстче, без «хвоста».
      const gap = Math.hypot(target.panX - cur.panX, target.panY - cur.panY);
      if (gap > FOLLOW_CATCHUP_PAN_PX) k = Math.max(k, 0.72);

      const next: BoardViewportState = {
        panX: cur.panX + (target.panX - cur.panX) * k,
        panY: cur.panY + (target.panY - cur.panY) * k,
        scale: cur.scale + (target.scale - cur.scale) * k,
      };

      const panLeft = Math.hypot(target.panX - next.panX, target.panY - next.panY);
      const scaleLeft = Math.abs(target.scale - next.scale);
      const settled = panLeft < FOLLOW_SETTLE_PAN_PX && scaleLeft < FOLLOW_SETTLE_SCALE;

      applyingFollowRef.current = true;
      if (settled) {
        targetRef.current = null;
        vpRef.current.applyFollowViewport(target, { syncReact: true });
        presenceRef.current?.refreshPeerCursors?.();
        return;
      }

      reactEveryRef.current += dt;
      const syncReact = reactEveryRef.current >= 100;
      if (syncReact) reactEveryRef.current = 0;
      vpRef.current.applyFollowViewport(next, { syncReact });
      presenceRef.current?.refreshPeerCursors?.();
      rafRef.current = requestAnimationFrame(tick);
    };

    lastTickRef.current = performance.now();
    rafRef.current = requestAnimationFrame(tick);
  }, [presenceRef]);

  const setFollowTarget = useCallback(
    (viewport: BoardViewportState) => {
      if (!isValidViewport(viewport)) return;
      targetRef.current = {
        panX: viewport.panX,
        panY: viewport.panY,
        scale: viewport.scale,
      };
      ensureFollowLoop();
    },
    [ensureFollowLoop]
  );

  const updateFollowFromPeer = useCallback(
    (peerId: string, data?: { viewport?: BoardViewportState | null; cursor?: StagePoint | null }) => {
      if (followPeerIdRef.current !== peerId) return;
      const peerVp =
        (data?.viewport && isValidViewport(data.viewport) ? data.viewport : null) ||
        presenceRef.current?.getPeerViewport?.(peerId) ||
        null;
      if (peerVp) setFollowTarget(peerVp);
      // Курсор не двигает камеру — только обновляем отрисовку, если уже на месте.
      if (isValidCursor(data?.cursor ?? null)) {
        presenceRef.current?.refreshPeerCursors?.();
      }
    },
    [presenceRef, setFollowTarget]
  );

  const startFollow = useCallback(
    (peerId: string) => {
      if (followPeerIdRef.current === peerId) {
        clearFollow();
        return;
      }
      followPeerIdRef.current = peerId;
      setFollowPeerId(peerId);
      updateFollowFromPeer(peerId);
    },
    [clearFollow, updateFollowFromPeer]
  );

  const onPeerPresence = useCallback(
    (
      peerId: string,
      data: { viewport?: BoardViewportState | null; cursor?: StagePoint | null }
    ) => {
      updateFollowFromPeer(peerId, data);
    },
    [updateFollowFromPeer]
  );

  useEffect(() => {
    if (!ready) return;
    if (applyingFollowRef.current) {
      applyingFollowRef.current = false;
      return;
    }
    if (followPeerIdRef.current) return;
  }, [ready, vp.viewport.panX, vp.viewport.panY, vp.viewport.scale]);

  useEffect(() => {
    function onUserCamera() {
      if (followPeerIdRef.current) clearFollow();
    }
    window.addEventListener("wb:user-camera", onUserCamera);
    window.addEventListener("wb:viewport-gesture-start", onUserCamera);
    return () => {
      window.removeEventListener("wb:user-camera", onUserCamera);
      window.removeEventListener("wb:viewport-gesture-start", onUserCamera);
    };
  }, [clearFollow]);

  useEffect(() => {
    if (!followPeerId) return;
    const stillThere = participants.some((p) => p.peerId === followPeerId && !p.isSelf);
    if (!stillThere) clearFollow();
  }, [participants, followPeerId, clearFollow]);

  useEffect(() => {
    return () => stopFollowLoop();
  }, [stopFollowLoop]);

  return {
    followPeerId,
    startFollow,
    clearFollow,
    onPeerPresence,
  };
}
